import type { AxiosError } from 'axios';
import { Button, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { Department } from '@/api/generated/models';
import {
  getGetDepartmentQueryKey,
  getListDepartmentsQueryKey,
  useCreateDepartment,
  useUpdateDepartment
} from '@/api/generated/departments-teams/departments-teams';
import {
  FormModal,
  ModalFooter,
  MutationButton,
  TextareaField,
  TextField,
  Toast
} from '@/components/common';

const schema = z.object({ name: z.string().min(1), description: z.string().optional() });
type FormValues = z.infer<typeof schema>;
const emptyValues: FormValues = { name: '', description: '' };

const departmentErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface DepartmentFormModalProps {
  opened: boolean;
  onClose: () => void;
  department?: Department;
  onCreated?: (id: string) => void;
  onSaved?: (message: string) => void;
}

export const DepartmentFormModal = ({
  opened,
  onClose,
  department,
  onCreated,
  onSaved
}: DepartmentFormModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = !!department;

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: emptyValues
  });

  useEffect(() => {
    if (opened) {
      form.setValues(
        department
          ? { name: department.name ?? '', description: department.description ?? '' }
          : emptyValues
      );
    }
  }, [opened, department]);

  const createMutation = useCreateDepartment({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: (created) => {
        queryClient.invalidateQueries({ queryKey: getListDepartmentsQueryKey() });
        const createdId = (created as Department | undefined)?.id;
        onClose();
        onSaved?.(t('departments.createSuccess'));
        if (createdId) onCreated?.(createdId);
      }
    }
  });

  const updateMutation = useUpdateDepartment({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListDepartmentsQueryKey() });
        if (department?.id) {
          queryClient.invalidateQueries({ queryKey: getGetDepartmentQueryKey(department.id) });
        }
        onSaved?.(t('departments.updateSuccess'));
        onClose();
      }
    }
  });

  const mutation = isEdit ? updateMutation : createMutation;

  const handleSubmit = (values: FormValues) => {
    if (isEdit && department?.id) {
      updateMutation.mutate({
        id: department.id,
        data: { name: values.name, colour: department.colour, description: values.description }
      });
    } else {
      createMutation.mutate({ data: { name: values.name, description: values.description } });
    }
  };

  return (
    <FormModal
      opened={opened}
      onClose={onClose}
      title={
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {isEdit ? t('departments.editDepartment') : t('departments.newDepartment')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap={16}>
          {mutation.isError && (
            <Toast tone="danger">{departmentErrorMessage(mutation.error, t('common.error'))}</Toast>
          )}

          <TextField
            label={t('departments.fieldName')}
            required
            placeholder={t('departments.namePlaceholder')}
            {...form.getInputProps('name')}
          />

          <TextareaField
            label={t('departments.fieldDescription')}
            minRows={3}
            placeholder={t('departments.descriptionPlaceholder')}
            {...form.getInputProps('description')}
          />

          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <MutationButton
              type="submit"
              isPending={mutation.isPending}
              isPaused={mutation.isPaused}
            >
              {t('common.save')}
            </MutationButton>
          </ModalFooter>
        </Stack>
      </form>
    </FormModal>
  );
};
